import { useState, useEffect } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from '@/contexts/AuthContext'
import { Brain, ChevronDown, Check, Loader2, Globe } from 'lucide-react'
import { toast } from 'sonner'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { Button } from '@/components/ui/button'

const LLM_OPTIONS = {
  openai: {
    label: 'OpenAI',
    models: [
      { value: 'gpt-5.2', label: 'GPT-5.2 Thinking' },
      { value: 'gpt-5.2-codex', label: 'GPT-5.2 Codex' },
      { value: 'gpt-5.1', label: 'GPT-5.1' },
      { value: 'gpt-5', label: 'GPT-5' },
      { value: 'gpt-5-mini', label: 'GPT-5 Mini' },
      { value: 'gpt-5-nano', label: 'GPT-5 Nano' },
      { value: 'gpt-4o', label: 'GPT-4o' },
      { value: 'gpt-4o-mini', label: 'GPT-4o Mini' },
      { value: 'o3-mini', label: 'o3 Mini' },
    ],
  },
  anthropic: {
    label: 'Anthropic',
    models: [
      { value: 'claude-opus-4-6', label: 'Claude Opus 4.6' },
      { value: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.6' },
      { value: 'claude-sonnet-4-5-20250929', label: 'Claude Sonnet 4.5' },
      { value: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5' },
      { value: 'claude-opus-4-5-20251101', label: 'Claude Opus 4.5 (Legacy)' },
    ],
  },
} as const

type Provider = keyof typeof LLM_OPTIONS

function getModelLabel(provider: string, model: string): string {
  const providerConfig = LLM_OPTIONS[provider as Provider]
  if (!providerConfig) return model
  const modelConfig = providerConfig.models.find((m) => m.value === model)
  return modelConfig?.label || model
}

export function LLMModelSelector() {
  const { user } = useAuth()
  const [provider, setProvider] = useState<string>('openai')
  const [model, setModel] = useState<string>('gpt-4o')
  const [saving, setSaving] = useState(false)
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    if (!user?.id) return
    supabase
      .from('profiles')
      .select('llm_provider, llm_model')
      .eq('user_id', user.id)
      .single()
      .then(({ data }) => {
        if (data) {
          setProvider(data.llm_provider || 'openai')
          setModel(data.llm_model || 'gpt-4o')
        }
        setLoaded(true)
      })
  }, [user?.id])

  const handleSelect = async (newProvider: string, newModel: string) => {
    if (!user?.id) return
    if (newProvider === provider && newModel === model) return

    const prev = { provider, model }
    setProvider(newProvider)
    setModel(newModel)
    setSaving(true)

    const { error } = await supabase
      .from('profiles')
      .update({ llm_provider: newProvider, llm_model: newModel })
      .eq('user_id', user.id)

    setSaving(false)

    if (error) {
      // Roll back the optimistic UI so it doesn't lie about what's persisted.
      setProvider(prev.provider)
      setModel(prev.model)
      toast.error('No se pudo guardar el modelo. Intenta de nuevo.')
      return
    }

    // CA-043: this is a GLOBAL per-user setting — every automated cadence message
    // this user generates will now use this model. Make that consequence explicit
    // instead of letting a "just for a test" change silently switch all sends.
    toast.success(`Modelo cambiado a ${getModelLabel(newProvider, newModel)}`, {
      description: 'Aplica a TODA tu generación automatizada de mensajes, no solo a esta prueba.',
    })
  }

  if (!loaded) return null

  return (
    <TooltipProvider>
      <DropdownMenu>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-7 gap-1.5 text-xs font-normal">
                <Brain className="h-3 w-3" />
                {saving ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  getModelLabel(provider, model)
                )}
                <Globe className="h-3 w-3 text-muted-foreground" />
                <ChevronDown className="h-3 w-3 opacity-50" />
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="max-w-[240px] text-xs">
            Modelo global de tu cuenta. Se usa en TODA tu generación automatizada de mensajes de cadencia — cambiarlo aquí lo cambia en todos lados.
          </TooltipContent>
        </Tooltip>
        <DropdownMenuContent align="end" className="w-60">
          <DropdownMenuLabel className="flex items-start gap-1.5 text-[11px] font-normal text-muted-foreground whitespace-normal leading-snug">
            <Globe className="h-3 w-3 mt-0.5 shrink-0" />
            <span>Setting global de tu cuenta: afecta todos tus mensajes automatizados.</span>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {Object.entries(LLM_OPTIONS).map(([provKey, provConfig]) => (
            <div key={provKey}>
              <DropdownMenuLabel className="text-xs text-muted-foreground">
                {provConfig.label}
              </DropdownMenuLabel>
              {provConfig.models.map((m) => (
                <DropdownMenuItem
                  key={m.value}
                  onClick={() => handleSelect(provKey, m.value)}
                  className="flex items-center justify-between"
                >
                  <span>{m.label}</span>
                  {provider === provKey && model === m.value && (
                    <Check className="h-3.5 w-3.5 text-primary" />
                  )}
                </DropdownMenuItem>
              ))}
              {provKey === 'openai' && <DropdownMenuSeparator />}
            </div>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </TooltipProvider>
  )
}
